import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { wbsApi, projectApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Table, Badge, Spinner, Tabs } from '../components/UI';
import {
  Plus, PencilSimple, TreeStructure, Link as LinkIcon, Trash,
  CaretRight, CaretDown, FolderOpen, Folder, Circle, CheckCircle
} from '@phosphor-icons/react';

import { fmtCurrencyShort } from '../utils/currencyFormat';

export default function WBSPage() {
  const [activeTab, setActiveTab] = useState('library');
  const [activities, setActivities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectActivities, setProjectActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterTrade, setFilterTrade] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [trades, setTrades] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Tree state
  const [expandedTrades, setExpandedTrades] = useState({});
  const [expandedSubs, setExpandedSubs] = useState({});

  // Project tab
  const [selectedProject, setSelectedProject] = useState('');

  // Activity form
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({
    trade: '', subcategory: '', activity_name: '', unit: ''
  });

  // Link form
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkForm, setLinkForm] = useState({
    activity_id: '', planned_quantity: '', planned_cost: ''
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- Data loading ----
  const loadActivities = useCallback(async () => {
    try {
      const res = await wbsApi.listActivities(
        filterTrade || undefined,
        filterSubcategory || undefined
      );
      setActivities(res.data);
    } catch (err) {
      console.error('Failed to load activities:', err);
    }
  }, [filterTrade, filterSubcategory]);

  useEffect(() => {
    const init = async () => {
      try {
        const [activitiesRes, projectsRes, tradesRes] = await Promise.all([
          wbsApi.listActivities(),
          projectApi.list(),
          wbsApi.getTrades()
        ]);
        setActivities(activitiesRes.data);
        setProjects(projectsRes.data);
        setTrades(tradesRes.data.trades || []);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  // Load subcategories when trade filter changes
  useEffect(() => {
    if (filterTrade) {
      wbsApi.getSubcategories(filterTrade).then(res => {
        setSubcategories(res.data.subcategories || []);
      });
    } else {
      setSubcategories([]);
      setFilterSubcategory('');
    }
  }, [filterTrade]);

  useEffect(() => {
    if (selectedProject) {
      wbsApi.getProjectActivities(selectedProject).then(res => {
        setProjectActivities(res.data);
      });
    }
  }, [selectedProject]);

  const loadProjectActivities = async () => {
    if (!selectedProject) return;
    const res = await wbsApi.getProjectActivities(selectedProject);
    setProjectActivities(res.data);
  };

  // ---- Tree structure: Trade → Subcategory → Activities ----
  const tree = useMemo(() => {
    const map = {};
    activities.forEach(act => {
      const t = act.trade || 'Other';
      const s = act.subcategory || 'General';
      if (!map[t]) map[t] = {};
      if (!map[t][s]) map[t][s] = [];
      map[t][s].push(act);
    });
    // Sort trades alphabetically
    return Object.keys(map).sort().reduce((acc, t) => {
      acc[t] = Object.keys(map[t]).sort().reduce((sAcc, s) => {
        sAcc[s] = map[t][s];
        return sAcc;
      }, {});
      return acc;
    }, {});
  }, [activities]);

  const toggleTrade = (trade) => {
    setExpandedTrades(prev => ({ ...prev, [trade]: !prev[trade] }));
  };

  const toggleSub = (key) => {
    setExpandedSubs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    const tExp = {};
    const sExp = {};
    Object.keys(tree).forEach(t => {
      tExp[t] = true;
      Object.keys(tree[t]).forEach(s => { sExp[`${t}::${s}`] = true; });
    });
    setExpandedTrades(tExp);
    setExpandedSubs(sExp);
  };

  const collapseAll = () => {
    setExpandedTrades({});
    setExpandedSubs({});
  };

  // ---- Activity CRUD ----
  const openCreateActivity = (prefillTrade, prefillSub) => {
    setEditingActivity(null);
    setActivityForm({
      trade: prefillTrade || '',
      subcategory: prefillSub || '',
      activity_name: '',
      unit: ''
    });
    setError('');
    setShowActivityModal(true);
  };

  const openEditActivity = (act) => {
    setEditingActivity(act);
    setActivityForm({
      trade: act.trade,
      subcategory: act.subcategory,
      activity_name: act.activity_name,
      unit: act.unit || ''
    });
    setError('');
    setShowActivityModal(true);
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editingActivity) {
        await wbsApi.updateActivity(editingActivity.id, activityForm);
      } else {
        await wbsApi.createActivity(activityForm);
      }
      setShowActivityModal(false);
      loadActivities();
      const tradesRes = await wbsApi.getTrades();
      setTrades(tradesRes.data.trades || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  // ---- Link / Unlink ----
  const openLinkModal = () => {
    setLinkForm({ activity_id: '', planned_quantity: '', planned_cost: '' });
    setError('');
    setShowLinkModal(true);
  };

  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await wbsApi.linkActivity(selectedProject, {
        project_id: selectedProject,
        activity_id: linkForm.activity_id,
        planned_quantity: parseFloat(linkForm.planned_quantity) || 0,
        planned_cost: parseFloat(linkForm.planned_cost) || 0
      });
      setShowLinkModal(false);
      loadProjectActivities();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to link activity');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (linkId) => {
    if (!window.confirm('Remove this activity from the project?')) return;
    try {
      await wbsApi.unlinkActivity(selectedProject, linkId);
      loadProjectActivities();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to unlink');
    }
  };

  // Already-linked activity IDs for the selected project
  const linkedActivityIds = useMemo(
    () => new Set(projectActivities.map(pa => pa.activity_id)),
    [projectActivities]
  );

  // Quick-link from library tree
  const handleQuickLink = async (activityId) => {
    if (!selectedProject) return;
    try {
      await wbsApi.linkActivity(selectedProject, {
        project_id: selectedProject,
        activity_id: activityId,
        planned_quantity: 0,
        planned_cost: 0
      });
      loadProjectActivities();
    } catch (err) {
      alert(err.response?.data?.detail || 'Already linked or failed');
    }
  };

  const totalActivities = activities.length;
  const tradeCount = Object.keys(tree).length;

  const tabs = [
    { id: 'library', label: 'Activity Library' },
    { id: 'project', label: 'Project Activities' },
  ];

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" data-testid="wbs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Work Breakdown Structure</h1>
          <p className="text-[#666666]">
            {tradeCount} trade{tradeCount !== 1 ? 's' : ''}, {totalActivities} activit{totalActivities !== 1 ? 'ies' : 'y'}
          </p>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* ===== ACTIVITY LIBRARY TAB ===== */}
      {activeTab === 'library' && (
        <div className="space-y-4">
          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={filterTrade}
              onChange={(e) => { setFilterTrade(e.target.value); setFilterSubcategory(''); }}
              className="w-48"
              data-testid="filter-trade-select"
            >
              <option value="">All Trades</option>
              {trades.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>

            <Select
              value={filterSubcategory}
              onChange={(e) => setFilterSubcategory(e.target.value)}
              className="w-48"
              disabled={!filterTrade}
              data-testid="filter-subcategory-select"
            >
              <option value="">All Subcategories</option>
              {subcategories.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className="text-xs text-[#002FA7] hover:underline px-2 py-1"
                data-testid="expand-all-btn"
              >
                Expand All
              </button>
              <span className="text-[#E0E0E0]">|</span>
              <button
                onClick={collapseAll}
                className="text-xs text-[#666666] hover:underline px-2 py-1"
                data-testid="collapse-all-btn"
              >
                Collapse All
              </button>
            </div>

            <Button onClick={() => openCreateActivity('', '')} data-testid="add-activity-btn">
              <Plus className="w-4 h-4 mr-2" /> New Activity
            </Button>
          </div>

          {/* Tree view */}
          {tradeCount === 0 ? (
            <div className="text-center py-12 text-[#666666]">No activities found</div>
          ) : (
            <div className="space-y-2" data-testid="wbs-tree">
              {Object.entries(tree).map(([trade, subs]) => {
                const tradeExpanded = !!expandedTrades[trade];
                const tradeActCount = Object.values(subs).reduce((s, a) => s + a.length, 0);
                return (
                  <Card key={trade} className="overflow-hidden">
                    {/* Trade row */}
                    <button
                      className="w-full flex items-center justify-between p-3.5 hover:bg-[#F9F9F9] transition-colors text-left"
                      onClick={() => toggleTrade(trade)}
                      data-testid={`trade-row-${trade}`}
                    >
                      <div className="flex items-center space-x-3">
                        {tradeExpanded
                          ? <CaretDown className="w-4 h-4 text-[#002FA7]" weight="bold" />
                          : <CaretRight className="w-4 h-4 text-[#666666]" weight="bold" />
                        }
                        <div className="w-8 h-8 bg-[#002FA7] rounded-lg flex items-center justify-center">
                          <TreeStructure className="w-4 h-4 text-white" weight="bold" />
                        </div>
                        <div>
                          <span className="font-semibold text-[#0A0A0A]">{trade}</span>
                          <span className="text-xs text-[#666666] ml-2">
                            {Object.keys(subs).length} subcategor{Object.keys(subs).length !== 1 ? 'ies' : 'y'}
                          </span>
                        </div>
                      </div>
                      <Badge variant="primary">{tradeActCount}</Badge>
                    </button>

                    {/* Subcategories */}
                    {tradeExpanded && (
                      <div className="border-t border-[#E0E0E0]">
                        {Object.entries(subs).map(([sub, acts]) => {
                          const subKey = `${trade}::${sub}`;
                          const subExpanded = !!expandedSubs[subKey];
                          return (
                            <div key={subKey}>
                              <button
                                className="w-full flex items-center justify-between px-4 py-2.5 pl-10 hover:bg-[#FAFAFA] transition-colors text-left border-b border-[#F0F0F0] last:border-b-0"
                                onClick={() => toggleSub(subKey)}
                                data-testid={`subcategory-row-${trade}-${sub}`}
                              >
                                <div className="flex items-center space-x-2.5">
                                  {subExpanded
                                    ? <CaretDown className="w-3.5 h-3.5 text-[#002FA7]" />
                                    : <CaretRight className="w-3.5 h-3.5 text-[#666666]" />
                                  }
                                  {subExpanded
                                    ? <FolderOpen className="w-4 h-4 text-[#FFAB00]" weight="fill" />
                                    : <Folder className="w-4 h-4 text-[#FFAB00]" weight="fill" />
                                  }
                                  <span className="text-sm font-medium text-[#333]">{sub}</span>
                                </div>
                                <span className="text-xs text-[#999]">{acts.length}</span>
                              </button>

                              {/* Activities */}
                              {subExpanded && (
                                <div className="bg-[#FAFAFA]">
                                  {acts.map(act => (
                                    <div
                                      key={act.id}
                                      className="flex items-center justify-between px-4 py-2 pl-20 border-b border-[#F0F0F0] last:border-b-0 hover:bg-[#F3F3F3] transition-colors"
                                      data-testid={`activity-row-${act.id}`}
                                    >
                                      <div className="flex items-center space-x-2">
                                        <Circle className="w-2.5 h-2.5 text-[#002FA7]" weight="fill" />
                                        <span className="text-sm text-[#0A0A0A]">{act.activity_name}</span>
                                        {act.unit && (
                                          <span className="text-[10px] text-[#666666] bg-[#E5E5E5] px-1.5 py-0.5 rounded font-mono">
                                            {act.unit}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        {/* Quick-link to project if Project Activities tab is also being used */}
                                        {selectedProject && activeTab === 'library' && !linkedActivityIds.has(act.id) && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleQuickLink(act.id); }}
                                            className="p-1 text-[#00C853] hover:bg-[#00C853]/10 rounded transition-colors"
                                            title="Link to selected project"
                                            data-testid={`quick-link-${act.id}`}
                                          >
                                            <LinkIcon className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                        <button
                                          onClick={() => openEditActivity(act)}
                                          className="p-1 text-[#666666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 rounded transition-colors"
                                          data-testid={`edit-activity-btn-${act.id}`}
                                        >
                                          <PencilSimple className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Add activity within this subcategory */}
                                  <button
                                    onClick={() => openCreateActivity(trade, sub)}
                                    className="w-full flex items-center space-x-2 px-4 py-2 pl-20 text-xs text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors"
                                    data-testid={`add-activity-in-${trade}-${sub}`}
                                  >
                                    <Plus className="w-3 h-3" />
                                    <span>Add activity in {sub}</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== PROJECT ACTIVITIES TAB ===== */}
      {activeTab === 'project' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-64"
              data-testid="select-project-wbs"
            >
              <option value="">-- Select Project --</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            {selectedProject && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#666666]" data-testid="linked-count">
                  {projectActivities.length} linked
                </span>
                <Button onClick={openLinkModal} data-testid="link-activity-btn">
                  <LinkIcon className="w-4 h-4 mr-2" /> Link Activity
                </Button>
              </div>
            )}
          </div>

          {!selectedProject ? (
            <Card className="p-8">
              <div className="text-center text-[#666666]">
                <TreeStructure className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />
                <p>Select a project to manage its linked activities</p>
              </div>
            </Card>
          ) : projectActivities.length === 0 ? (
            <Card className="p-8">
              <div className="text-center text-[#666666]">
                <p>No activities linked yet.</p>
                <p className="text-sm mt-1">Click "Link Activity" to assign WBS activities to this project.</p>
              </div>
            </Card>
          ) : (
            <Card>
              <Table
                columns={[
                  {
                    header: 'Activity',
                    render: (row) => (
                      <div className="flex items-center space-x-2" data-testid={`project-activity-name-${row.id}`}>
                        <CheckCircle className="w-4 h-4 text-[#002FA7] shrink-0" weight="fill" />
                        <div>
                          <p className="font-medium text-[#0A0A0A]">{row.activity_details?.activity_name || 'Unknown'}</p>
                          <p className="text-xs text-[#666666]">{row.activity_details?.trade} / {row.activity_details?.subcategory}</p>
                        </div>
                      </div>
                    )
                  },
                  {
                    header: 'Unit',
                    render: (row) => (
                      <span className="text-[11px] bg-[#E5E5E5] px-1.5 py-0.5 rounded font-mono">
                        {row.activity_details?.unit || '-'}
                      </span>
                    )
                  },
                  {
                    header: 'Planned Qty',
                    render: (row) => <span data-testid={`pa-qty-${row.id}`}>{row.planned_quantity || 0}</span>
                  },
                  {
                    header: 'Planned Cost',
                    render: (row) => (
                      <span data-testid={`pa-cost-${row.id}`}>
                        {row.planned_cost ? fmtCurrencyShort(row.planned_cost) : '-'}
                      </span>
                    )
                  },
                  {
                    header: 'Status',
                    render: (row) => (
                      <Badge variant={
                        row.status === 'COMPLETED' ? 'success' :
                        row.status === 'IN_PROGRESS' ? 'warning' : 'default'
                      }>
                        {row.status}
                      </Badge>
                    )
                  },
                  {
                    header: '',
                    render: (row) => (
                      <button
                        onClick={() => handleUnlink(row.id)}
                        className="p-1.5 text-[#666666] hover:text-[#FF3333] hover:bg-[#FF3333]/5 rounded transition-colors"
                        title="Unlink from project"
                        data-testid={`unlink-activity-btn-${row.id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )
                  }
                ]}
                data={projectActivities}
              />
            </Card>
          )}
        </div>
      )}

      {/* ===== Activity Create/Edit Modal ===== */}
      <Modal isOpen={showActivityModal} onClose={() => setShowActivityModal(false)} title={editingActivity ? 'Edit Activity' : 'New Activity'}>
        <form onSubmit={handleActivitySubmit} className="space-y-4" data-testid="activity-form">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm" data-testid="activity-form-error">{error}</div>}

          <Input
            label="Trade"
            value={activityForm.trade}
            onChange={(e) => setActivityForm({ ...activityForm, trade: e.target.value })}
            required
            placeholder="e.g. Civil, Electrical, Plumbing"
            data-testid="activity-trade-input"
          />
          <Input
            label="Subcategory"
            value={activityForm.subcategory}
            onChange={(e) => setActivityForm({ ...activityForm, subcategory: e.target.value })}
            required
            placeholder="e.g. Excavation, Wiring, Piping"
            data-testid="activity-subcategory-input"
          />
          <Input
            label="Activity Name"
            value={activityForm.activity_name}
            onChange={(e) => setActivityForm({ ...activityForm, activity_name: e.target.value })}
            required
            placeholder="e.g. Foundation Concrete"
            data-testid="activity-name-input"
          />
          <Input
            label="Unit of Measure"
            value={activityForm.unit}
            onChange={(e) => setActivityForm({ ...activityForm, unit: e.target.value })}
            placeholder="SQM, CUM, RMT, NOS, KG"
            data-testid="activity-unit-input"
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowActivityModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-activity-btn">
              {saving ? 'Saving...' : editingActivity ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ===== Link Activity to Project Modal ===== */}
      <Modal isOpen={showLinkModal} onClose={() => setShowLinkModal(false)} title="Link Activity to Project">
        <form onSubmit={handleLinkSubmit} className="space-y-4" data-testid="link-form">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm" data-testid="link-form-error">{error}</div>}

          <Select
            label="Activity"
            value={linkForm.activity_id}
            onChange={(e) => setLinkForm({ ...linkForm, activity_id: e.target.value })}
            required
            data-testid="link-activity-select"
          >
            <option value="">-- Select Activity --</option>
            {activities
              .filter(a => !linkedActivityIds.has(a.id))
              .map(a => (
                <option key={a.id} value={a.id}>
                  {a.trade} / {a.subcategory} — {a.activity_name} ({a.unit || 'N/A'})
                </option>
              ))
            }
          </Select>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Planned Quantity"
              type="number"
              min="0"
              step="any"
              value={linkForm.planned_quantity}
              onChange={(e) => setLinkForm({ ...linkForm, planned_quantity: e.target.value })}
              placeholder="0"
              data-testid="link-quantity-input"
            />
            <Input
              label="Planned Cost ($)"
              type="number"
              min="0"
              step="any"
              value={linkForm.planned_cost}
              onChange={(e) => setLinkForm({ ...linkForm, planned_cost: e.target.value })}
              placeholder="0"
              data-testid="link-cost-input"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowLinkModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-link-btn">
              {saving ? 'Linking...' : 'Link Activity'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
